import React, { useCallback, useMemo } from "react";

import Select from "antd/es/select";
import { useIntl } from "react-intl";
import invariant from "tiny-invariant";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFiltersConfig from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import FilterModel, {
  FilterCondition,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import FilterOperatorAction from "@app/types/FilterOperatorAction";
import { getFilterOperatorDisplayName } from "@app/util/ui";
import { isCombinedCondition } from "@app/util/viewModel/assert";

interface Props {
  className?: string;
  disabled?: boolean;
  field: IField;
  filterModel: Partial<FilterModel>;
  filterOperators: FilterOperatorAction;
  onChange?: (newCondition: SimpleCondition) => void;
}

const OperatorColumn: React.FC<Props> = ({
  className,
  disabled,
  field,
  filterModel,
  filterOperators,
  onChange,
}) => {
  const intl = useIntl();

  const filterCondition: FilterCondition | undefined = filterModel[field.filterName];
  invariant(
    !filterCondition || (filterCondition && !isCombinedCondition(filterCondition)),
    `Combined conditions are not yet supported in filter bar`
  );

  const currentOperator = useMemo(() => {
    if (filterCondition?.operator) {
      return filterCondition.operator;
    }
    const operators = filterOperators(field, (field.filter as IFiltersConfig).operators).map(
      ({ operator }) => operator
    );

    if (operators.includes((field.filter as IFiltersConfig).defaultOperator)) {
      return (field.filter as IFiltersConfig).defaultOperator;
    }
    return operators[0];
  }, [field, filterCondition?.operator, filterOperators]);

  const operatorOptions = useMemo(
    () =>
      filterOperators(field, (field.filter as IFiltersConfig).operators).map(({ operator }) => ({
        label: getFilterOperatorDisplayName(intl, operator, field),
        value: operator,
      })),
    [field, filterOperators, intl]
  );
  const value = filterCondition?.value;

  const handleChangeOperator = useCallback(
    (newOperator: FilterOperator) => {
      // check if new instance supports current value
      // (e.g. it can be "equal" with text value and "is known" with no value
      // if new instance doesn't support current value, then reset value
      const newInstance = (field.filter as IFiltersConfig).filterInstances[
        (field.filter as IFiltersConfig).operators.find(({ operator }) => operator === newOperator)!
          .instance
      ];

      if (!newInstance.instance.doesSupportValue?.(value, newOperator)) {
        onChange?.({ operator: newOperator, value: undefined });
      } else {
        onChange?.({ operator: newOperator, value: filterCondition?.value });
      }
    },
    [field, filterCondition, onChange, value]
  );

  return (
    <Select<FilterOperator>
      className={className}
      disabled={operatorOptions.length < 2 || disabled}
      dropdownMatchSelectWidth={false}
      onChange={handleChangeOperator}
      optionFilterProp="label"
      options={operatorOptions}
      showSearch
      value={currentOperator}
    />
  );
};

export default OperatorColumn;
